import type { ClinicalProviderResult as DirectClinicalProviderResult } from "@/app/directories/companion/components_v2/PatientHeader/UI/ClinicalProviderWidget";
import { formatPhoneNumber, normalizePhoneInput } from "@/util/formatters";
import { useIntake } from "@frontend/components/intake/contexts/useIntake";
import { searchForClinicalProvider as directSearchForClinicalProvider } from "@frontend/lib/actions/actions";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { Button } from "@frontend/shared/components/ui/button";
import { cn } from "@frontend/shared/lib/utils";
import { Loader2, Pencil, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useDebounceValue } from "usehooks-ts";
import { formatRequiredLabel } from "./IntakeFormQuestion";

export type ClinicalProviderSearchResult = {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  name?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  ehrId?: number;
  ncpdpId?: number;
};

export type ClinicalProviderFormData = {
  name: string;
  firstName?: string;
  lastName?: string;
  address: {
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  phone?: string;
  ehrId?: number;
  ncpdpId?: number;
};

const searchClinicalProvider = async (
  endpoint: string,
  searchTerm: string,
  searchZip: string,
  limit = 20,
  radius = 10,
) => {
  const response = await fetch(
    `${endpoint}?name=${encodeURIComponent(
      searchTerm,
    )}&zip=${encodeURIComponent(searchZip)}&limit=${limit}&radius=${radius}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "*/*",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to search: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

// Helper function to map DirectClinicalProviderResult to ClinicalProviderSearchResult
const mapDirectResultToSearchResult = (
  result: DirectClinicalProviderResult,
): ClinicalProviderSearchResult => {
  return {
    streetAddress: result.address,
    city: result.city,
    state: result.state,
    zip: result.zip,
    name: result.name,
    phone: result.phone,
    firstName: result.firstname,
    lastName: result.lastname,
    ehrId: result.clinicalproviderid
      ? Number(result.clinicalproviderid)
      : undefined,
    ncpdpId: result.ncpdpid,
  };
};

export const IntakeClinicalProviderInput = ({
  field,
  setValue,
  error,
  questionText,
  isRequired,
  clearErrors,
  hideQuestionText,
  isAdminView,
  questionAttributes,
  getValues,
}) => {
  const formattedLabel = formatRequiredLabel(
    questionText,
    isRequired,
    hideQuestionText,
  );

  // Local state
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<
    ClinicalProviderSearchResult[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchFinished, setSearchFinished] = useState(false);
  const t = useTranslations("TypeformIntake");
  const t_account = useTranslations("AccountProfile");

  const addressQuestionId = questionAttributes.addressQuestionId;
  const consumerLocalZip = addressQuestionId
    ? getValues(addressQuestionId.toString())
    : undefined;
  const { consumerZip, orgId } = useIntake();
  const nonNullConsumerZip = consumerLocalZip ?? consumerZip;
  const cleanedZip = nonNullConsumerZip?.includes("-")
    ? nonNullConsumerZip.split("-")[0]
    : nonNullConsumerZip;
  const [searchZip, setSearchZip] = useState(cleanedZip || "");
  const mode = questionAttributes.mode; // "pcp" or "pharmacy" or "directPharmacy"
  const searchEndpoint = questionAttributes.clinicalProviderOptionsEndpoint;
  const searchMode = questionAttributes.searchMode || "auto"; // "auto" | "button-search"
  const showManualAfterPages = 2; // Number of pages to show before allowing manual entry

  // Pagination state for button-search mode
  const [searchPage, setSearchPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);

  // Initialize form data from field value
  const initialFormData = useMemo(() => {
    if (!field.value) {
      return {
        name: "",
        firstName: "",
        lastName: "",
        address: {
          streetAddress: "",
          city: "",
          state: "",
          zip: "",
        },
        phone: "",
        ehrId: undefined,
        ncpdpId: undefined,
      };
    }

    try {
      if (typeof field.value === "string") {
        return JSON.parse(field.value) as ClinicalProviderFormData;
      }
      return field.value as ClinicalProviderFormData;
    } catch (e) {
      console.error("Error parsing clinical provider value:", e);
      return {
        name: "",
        firstName: "",
        lastName: "",
        address: {
          streetAddress: "",
          city: "",
          state: "",
          zip: "",
        },
        phone: "",
        ehrId: undefined,
        ncpdpId: undefined,
      };
    }
  }, [field.value]);

  const [formData, setFormData] =
    useState<ClinicalProviderFormData>(initialFormData);

  const incompleteClinicalProvider = (response: ClinicalProviderFormData) => {
    if (response.ehrId && response.ehrId.toString() !== "") {
      return false;
    }
    if (mode === "pcp") {
      return (
        !response.lastName ||
        !response.address.zip ||
        response.address.zip?.length !== 5
      );
    }
    return (
      !response.name ||
      !response.address.zip ||
      response.address.zip?.length !== 5
    );
  };

  // Initialize manual entry state based on ehrId
  const initialIsManualEntry = useMemo(() => {
    return (
      !initialFormData.ehrId &&
      (initialFormData.name || initialFormData.lastName) &&
      initialFormData.address.zip
    );
  }, [initialFormData]);

  const [isManualEntry, setIsManualEntry] = useState(!!initialIsManualEntry);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownScrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Form fields state
  const [debouncedSearchValue] = useDebounceValue(searchValue, 300);

  // Update form field value when formData changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: don't want to put function in dependency array
  useEffect(() => {
    if (error && field.value && !incompleteClinicalProvider(formData)) {
      clearErrors(field.name);
    }
    const newValue = formData;
    if (JSON.stringify(newValue) !== JSON.stringify(field.value)) {
      if (incompleteClinicalProvider(newValue)) {
        setValue(field.name, null);
      } else {
        setValue(field.name, newValue);
      }
    }
  }, [formData, setValue, field.name, field.value, error, clearErrors]);

  // Search handler for button-search mode
  const searchItemsButtonMode = useCallback(
    async (searchTerm: string, searchZip: string, page: number) => {
      if (!searchTerm.trim() || searchZip?.length !== 5 || !orgId) {
        // Only close dropdown on first page if invalid, keep open when loading more pages
        if (page === 0) {
          setShowDropdown(false);
          setSearchResults([]);
        }
        return;
      }
      setSearchFinished(false);

      try {
        setIsSearching(true);
        const currentPageResults = await directSearchForClinicalProvider(
          searchTerm,
          "",
          searchZip,
          orgId,
          page,
        );
        const nextPageResults = await directSearchForClinicalProvider(
          searchTerm,
          "",
          searchZip,
          orgId,
          page + 1,
        );

        const mappedResults = currentPageResults.map(
          mapDirectResultToSearchResult,
        );

        if (page === 0) {
          // First page - replace results
          setSearchResults(mappedResults);
        } else {
          // Store scroll position before appending
          const container = dropdownScrollRef.current;
          if (container) {
            scrollPositionRef.current = container.scrollTop;
          }
          // Subsequent pages - append results
          setSearchResults((prev) => [...prev, ...mappedResults]);

          // Restore scroll position after DOM update
          setTimeout(() => {
            if (container && scrollPositionRef.current > 0) {
              container.scrollTop = scrollPositionRef.current;
            }
          }, 0);
        }

        setHasMoreResults(nextPageResults.length > 0);
        setSearchFinished(true);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setSearchResults([]);
        setHasMoreResults(false);
      } finally {
        setIsSearching(false);
      }
    },
    [orgId],
  );

  // Search handler for auto mode
  const searchItems = useCallback(
    async (searchTerm: string, searchZip: string) => {
      if (!searchTerm.trim() || searchZip?.length !== 5) {
        setShowDropdown(false);
        setSearchResults([]);
        return;
      }
      setSearchFinished(false);

      try {
        setIsSearching(true);
        const data = await searchClinicalProvider(
          searchEndpoint,
          searchTerm,
          searchZip,
        );
        setSearchFinished(true);
        setSearchResults(data);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [searchEndpoint],
  );

  // Handle search value changes (only for auto mode)
  useEffect(() => {
    if (searchMode === "auto") {
      searchItems(debouncedSearchValue, searchZip);
    }
  }, [debouncedSearchValue, searchItems, searchZip, searchMode]);

  // Handle button search click
  const handleButtonSearch = () => {
    if (!searchValue.trim() || searchZip?.length !== 5) {
      return;
    }
    setSearchPage(0);
    setSearchResults([]);
    searchItemsButtonMode(searchValue, searchZip, 0);
    setShowDropdown(true);
  };

  // Handle infinite scroll - load more when near bottom (only for button-search mode)
  const handleScroll = useCallback(() => {
    if (searchMode !== "button-search") {
      return;
    }
    const container = dropdownScrollRef.current;
    if (!container || !hasMoreResults || isSearching) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // pixels from bottom

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      const nextPage = searchPage + 1;
      setSearchPage(nextPage);
      searchItemsButtonMode(searchValue, searchZip, nextPage);
    }
  }, [
    hasMoreResults,
    isSearching,
    searchPage,
    searchValue,
    searchZip,
    searchMode,
    searchItemsButtonMode,
  ]);

  // Set up scroll listener for infinite scroll (only for button-search mode)
  useEffect(() => {
    if (searchMode !== "button-search") {
      return;
    }
    const container = dropdownScrollRef.current;
    if (!container || !showDropdown) {
      return;
    }

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, showDropdown, searchMode]);

  // Handle selecting a search result
  const handleSelectResult = (result: ClinicalProviderSearchResult) => {
    const newFormData: ClinicalProviderFormData = {
      name: result.name || "",
      lastName: result.lastName,
      firstName: result.firstName,
      address: {
        streetAddress: result.streetAddress,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
      phone: result.phone,
      ehrId: result.ehrId ? result.ehrId : undefined,
      ncpdpId: result.ncpdpId ? result.ncpdpId : undefined,
    };
    console.log("newFormData", newFormData);
    setFormData(newFormData);
    setSearchValue("");
    setShowDropdown(false);
  };

  // Handle manual entry form changes
  const handleFormChange = (
    field: keyof ClinicalProviderFormData,
    inputValue: string | number | undefined,
  ) => {
    const value = inputValue || "";
    if (field === "firstName") {
      setFormData((prev) => ({
        ...prev,
        firstName: value ? value.toString() : "",
        name: `${value} ${prev.lastName || ""}`,
      }));
    } else if (field === "lastName") {
      setFormData((prev) => ({
        ...prev,
        lastName: value ? value.toString() : "",
        name: `${prev.firstName || ""} ${value}`,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleAddressChange = (
    field: keyof ClinicalProviderFormData["address"],
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchMode === "auto"
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchMode]);

  const handleReset = () => {
    const emptyFormData = {
      name: "",
      address: {
        streetAddress: "",
        city: "",
        state: "",
        zip: "",
      },
      phone: "",
      ehrId: undefined,
      ncpdpId: undefined,
    };
    setFormData(emptyFormData);
    setValue(field.name, null);
    setSearchValue("");
    setSearchPage(0);
    setHasMoreResults(false);
  };

  if (isAdminView) {
    const parsed = field.value ? JSON.parse(field.value) : null;
    const parts = parsed
      ? [
          parsed.name,
          parsed.address.streetAddress,
          parsed.address.city,
          parsed.address.state,
          parsed.address.zip,
          parsed.phone,
        ].filter(Boolean)
      : [];
    const value = parts.join(", ");
    return (
      <div className="w-full flex flex-col gap-1 relative">
        <p className="text-sm text-black">
          <label htmlFor={field.name}>{formattedLabel}</label>
        </p>
        <input
          type="text"
          value={value}
          disabled
          style={{
            height: "100%",
            backgroundColor: "lightgray",
          }}
          className={cn(
            "w-full border rounded bg-white/10 p-2.5 text-sm font-normal transition-all duration-200 focus:outline-none placeholder:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed",
          )}
        />
      </div>
    );
  }

  // If we have a selected value, show it with options to edit
  const hasSelectedValue =
    field.value &&
    formData.ehrId !== undefined &&
    formData.ehrId !== null &&
    formData.ehrId.toString() !== "";

  return (
    <div className="w-full flex flex-col gap-4 relative">
      {!hasSelectedValue && (
        <div className="flex flex-row gap-2 items-center justify-between">
          {!isManualEntry ? (
            <div className="flex flex-row gap-1 items-center">
              {
                <p
                  className={cn(
                    "text-sm text-gray-500 whitespace-nowrap",
                    searchZip?.length !== 5 && "text-red-600",
                  )}
                >
                  {searchZip?.length === 5 ? t("SearchZip") : t("EnterZip")}
                </p>
              }
              <input
                type="text"
                value={searchZip}
                onChange={(e) => {
                  setSearchFinished(false);
                  const newZip = e.target.value;
                  if (!/^\d*$/.test(newZip) || newZip?.length > 5) {
                    return;
                  }
                  setSearchZip(newZip);
                  // Only trigger search if we have a valid 5-digit zip and a search term (auto mode only)
                  if (
                    searchMode === "auto" &&
                    newZip?.length === 5 &&
                    /^\d+$/.test(newZip) &&
                    searchValue.trim()
                  ) {
                    setIsSearching(true);
                    searchItems(searchValue, newZip);
                    setShowDropdown(true);
                  } else if (searchMode === "button-search") {
                    // Reset pagination when zip changes in button mode
                    setSearchPage(0);
                    setSearchResults([]);
                    setHasMoreResults(false);
                  }
                }}
                placeholder=""
                className={cn(
                  "w-[50px] border-b border-black bg-white pl-1 py-0 mt-[1px] text-sm font-normal transition-all duration-200 outline-none focus:outline-none placeholder:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed",
                  searchZip?.length !== 5
                    ? "border-red-600"
                    : "focus:border-superscriptBlue",
                )}
              />
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-superscriptBlue hover:text-superscriptBlue/80 rounded-full px-4 py-2"
              onClick={() => {
                setIsManualEntry(false);
                setSearchValue("");
                setSearchResults([]);
                setIsSearching(false);
                setShowDropdown(false);
                setSearchPage(0);
                setHasMoreResults(false);
              }}
            >
              <p className="text-sm flex gap-2 items-center">
                <Search size={16} />
                {t("SearchInstead")}
              </p>
            </Button>
          )}
        </div>
      )}

      {hasSelectedValue ? (
        <div className="w-full flex flex-row gap-4 relative border border-pcv2-grey-stroke rounded-lg p-4">
          <div className="flex-grow">
            <p className="font-medium">{formData.name}</p>
            {formData.address.streetAddress && (
              <p className="text-sm text-gray-500">
                {formData.address.streetAddress}, {formData.address.city},{" "}
                {formData.address.state} {formData.address.zip}
              </p>
            )}
            {formData.phone && (
              <p className="text-sm text-gray-500">
                {formatPhoneNumber(normalizePhoneInput(formData.phone))}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            key="clear-selection-button"
            id="clear-selection-button"
            className="text-red-500 hover:text-red-600 rounded-full px-4 py-2"
            onClick={handleReset}
          >
            <p className="text-sm flex gap-2 items-center">
              <Trash2 size={16} />
            </p>
          </Button>
        </div>
      ) : !isManualEntry ? (
        <div className="flex flex-col gap-4" ref={dropdownRef}>
          <div className="flex-grow relative">
            <div
              className={cn(
                "flex flex-row gap-2 items-center",
                searchMode === "button-search" && "flex-row",
              )}
            >
              <input
                type="text"
                className={cn(
                  "rounded-full border border-black bg-white p-4 text-base font-medium placeholder:text-gray-500 focus:outline-none transition-all duration-200 focus:ring-0 placeholder:text-pcv2-grey-text placeholder:font-light",
                  !searchValue && "bg-gray-100 border-none",
                  searchMode === "button-search" ? "flex-grow" : "w-full",
                )}
                placeholder={
                  searchMode === "button-search"
                    ? t("SearchForYourPharmacyPlaceholder")
                    : t("StartTypingToSearch")
                }
                value={searchValue}
                onChange={(e) => {
                  setSearchFinished(false);
                  if (searchMode === "auto") {
                    setIsSearching(true);
                  }
                  setSearchValue(e.target.value);
                  if (searchMode === "auto") {
                    setShowDropdown(e.target.value?.length > 0);
                  } else {
                    // Reset pagination when search value changes in button mode
                    setSearchPage(0);
                    setSearchResults([]);
                    setHasMoreResults(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    searchMode === "button-search" &&
                    e.key === "Enter" &&
                    searchValue.trim() &&
                    searchZip?.length === 5
                  ) {
                    e.preventDefault();
                    handleButtonSearch();
                  }
                }}
                aria-label={"search-zip-code-input"}
              />
              {searchMode === "button-search" && (
                <Button
                  type="button"
                  onClick={handleButtonSearch}
                  disabled={
                    !searchValue.trim() ||
                    searchZip?.length !== 5 ||
                    isSearching ||
                    !orgId
                  }
                  className="rounded-full px-12 py-4 !h-full bg-superscriptBlue text-white hover:bg-superscriptBlue/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSearching ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Search size={20} />
                  )}
                </Button>
              )}
            </div>

            {/* Dropdown */}
            {showDropdown &&
              searchValue?.length > 0 &&
              searchZip?.length === 5 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 flex flex-col">
                  {isSearching && searchResults.length === 0 ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="animate-spin" size={20} />
                    </div>
                  ) : searchResults?.length > 0 ? (
                    <>
                      {/* Scrollable results area */}
                      <div
                        ref={dropdownScrollRef}
                        className="flex-1 overflow-y-auto"
                      >
                        {searchResults.map((result, index) => (
                          <button
                            key={`${result.ehrId}-${index}`}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-all duration-200 border-b border-pcv2-grey-stroke"
                            onClick={() => handleSelectResult(result)}
                          >
                            <div className="font-medium">{result.name}</div>
                            <div className="text-sm text-gray-500">
                              {result.streetAddress}, {result.city},{" "}
                              {result.state} {result.zip}
                            </div>
                          </button>
                        ))}
                        {/* Loading more indicator */}
                        {searchMode === "button-search" &&
                          isSearching &&
                          searchResults.length > 0 && (
                            <div className="flex items-center justify-center p-2 border-t border-pcv2-grey-stroke">
                              <Loader2 className="animate-spin" size={16} />
                              <span className="ml-2 text-sm text-gray-500">
                                {t("LoadingMoreResults")}
                              </span>
                            </div>
                          )}
                      </div>
                      {/* Sticky footer with "Not seeing" text and "Enter Manually" button - show after specified pages or when no more results (button-search mode only) */}
                      {searchMode === "button-search" &&
                        (searchPage >= showManualAfterPages ||
                          !hasMoreResults) && (
                          <div className="border-t border-pcv2-grey-stroke p-4 bg-white rounded-b-3xl sticky bottom-0 flex items-center gap-1 justify-center">
                            <p className="text-sm text-gray-500 text-center">
                              {mode === "pcp"
                                ? t("NotSeeingYourPCP")
                                : t("NotSeeingYourPharmacy")}
                            </p>
                            <div className="flex flex-row gap-2 items-center justify-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-superscriptBlue hover:text-superscriptBlue/80 rounded-full px-3"
                                onClick={() => setIsManualEntry(true)}
                              >
                                <p className="text-sm flex gap-2 items-center">
                                  <Pencil size={16} />
                                  {t("EnterManually")}
                                </p>
                              </Button>
                            </div>
                          </div>
                        )}
                    </>
                  ) : (
                    searchFinished && (
                      <div className="flex flex-col items-center justify-center p-4">
                        <p className="text-sm text-gray-500">
                          {t("NoResultsFound")}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-superscriptBlue hover:text-superscriptBlue/80 rounded-full px-4 py-2"
                          onClick={() => setIsManualEntry(true)}
                        >
                          <p className="text-sm flex gap-2 items-center">
                            <Pencil size={16} />
                            {t("EnterManuallyQuestion")}
                          </p>
                        </Button>
                      </div>
                    )
                  )}
                </div>
              )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-wrap gap-x-[42px] gap-y-4 w-full">
            {mode === "pharmacy" ? (
              <div className="w-full">
                <p className="text-xs text-gray-500">{t_account("Name")}</p>
                <input
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder=""
                  className={cn(
                    "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                    formData.name?.length > 0
                      ? "bg-white border-black border"
                      : !error && "border-none",
                    error && !formData.name && "border border-red-600",
                  )}
                />
              </div>
            ) : (
              <div className="w-full">
                <div className="flex gap-2 mt-2">
                  <div className="flex-grow">
                    <p className="text-xs text-gray-500">
                      {t_account("FirstName")}
                    </p>
                    <input
                      value={formData.firstName}
                      onChange={(e) =>
                        handleFormChange("firstName", e.target.value)
                      }
                      placeholder=""
                      className={cn(
                        "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                        formData.firstName && formData.firstName?.length > 0
                          ? "bg-white border-black border"
                          : "border-none",
                      )}
                    />
                  </div>
                  <div className="flex-grow">
                    <p className="text-xs text-gray-500">
                      {t_account("LastName")}
                    </p>
                    <input
                      value={formData.lastName}
                      onChange={(e) =>
                        handleFormChange("lastName", e.target.value)
                      }
                      placeholder=""
                      className={cn(
                        "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                        formData.lastName && formData.lastName?.length > 0
                          ? "bg-white border-black border"
                          : !error && "border-none",
                        error && !formData.lastName && "border border-red-600",
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="w-full">
              <p className="text-xs text-gray-500">{t_account("Phone")}</p>
              <input
                value={
                  !formData.phone
                    ? ""
                    : isValidPhoneNumber(formData.phone)
                      ? formatPhoneNumber(formData.phone)
                      : formData.phone
                }
                onChange={(e) => {
                  // block addition of letters
                  e.preventDefault();
                  const newChar = e.target.value.slice(-1);
                  if (/[a-zA-Z]/.test(newChar)) {
                    return;
                  }
                  const phone = isValidPhoneNumber(
                    normalizePhoneInput(e.target.value),
                  )
                    ? formatPhoneNumber(normalizePhoneInput(e.target.value))
                    : e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    phone: phone,
                  }));
                }}
                placeholder="(XXX) XXX-XXXX"
                type="tel"
                className={cn(
                  "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                  formData.phone && formData.phone?.length > 0
                    ? "bg-white border-black border"
                    : "border-none",
                )}
              />
            </div>
            <div className="w-full">
              <p className="text-xs text-gray-500">{t_account("Address")}</p>
              <div className="flex flex-col gap-2 w-full">
                <input
                  value={formData.address.streetAddress}
                  onChange={(e) =>
                    handleAddressChange("streetAddress", e.target.value)
                  }
                  placeholder=""
                  className={cn(
                    "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                    formData.address.streetAddress &&
                      formData.address.streetAddress?.length > 0
                      ? "bg-white border-black border"
                      : "border-none",
                  )}
                />
                <div className="flex gap-2 mt-2">
                  <div className="flex-grow">
                    <p className="text-xs text-gray-500">{t_account("City")}</p>
                    <input
                      value={formData.address.city}
                      onChange={(e) =>
                        handleAddressChange("city", e.target.value)
                      }
                      style={{ flex: 4 }}
                      placeholder=""
                      className={cn(
                        "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                        formData.address.city &&
                          formData.address.city?.length > 0
                          ? "bg-white border-black border"
                          : "border-none",
                      )}
                    />
                  </div>
                  <div className="flex-grow">
                    <p className="text-xs text-gray-500">
                      {t_account("State")}
                    </p>
                    <input
                      value={formData.address.state}
                      onChange={(e) =>
                        handleAddressChange("state", e.target.value)
                      }
                      style={{ flex: 2 }}
                      placeholder=""
                      className={cn(
                        "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                        formData.address.state &&
                          formData.address.state?.length > 0
                          ? "bg-white border-black border"
                          : "border-none",
                      )}
                    />
                  </div>
                </div>
                <div className="flex-grow mt-2">
                  <p className="text-xs text-gray-500">{t_account("Zip")}</p>
                  <input
                    maxLength={5}
                    value={formData.address.zip}
                    onChange={(e) => {
                      // block addition of letters
                      if (/[a-zA-Z]/.test(e.target.value)) {
                        return;
                      }
                      e.preventDefault();
                      handleAddressChange("zip", e.target.value);
                    }}
                    placeholder=""
                    className={cn(
                      "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light flex items-center w-full focus:outline-none transition-all duration-200",
                      formData.address.zip && formData.address.zip?.length > 0
                        ? "bg-white border-black border"
                        : !error && "border-none",
                      error &&
                        (!formData.address.zip ||
                          formData.address.zip?.length !== 5) &&
                        "border border-red-600",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
